import type { OrgRole, ProfileRow } from "./adminTypes";
import { ORG_ROLE_LABELS, formatAdminProfileName, orgRolesForAddDropdown } from "./adminTypes";

export type AdminRolePendingBlockProps = {
  approvedRolePending: ProfileRow[];
  rolesByUserId: Map<string, string[]>;
  loading: boolean;
  getRoleFor: (profileId: string) => OrgRole;
  setRoleByUser: React.Dispatch<React.SetStateAction<Record<string, OrgRole>>>;
  blockReasonByUser: Record<string, string>;
  setBlockReasonByUser: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  canDeleteUser: boolean;
  canAssignManufacturingOrgRole: boolean;
  onAssignRole: (profileId: string) => void;
  onBlock: (profileId: string, name: string) => void;
  onDeleteUser: (profileId: string, name: string) => void;
};

export function AdminRolePendingBlock({
  approvedRolePending,
  rolesByUserId,
  loading,
  getRoleFor,
  setRoleByUser,
  blockReasonByUser,
  setBlockReasonByUser,
  canDeleteUser,
  canAssignManufacturingOrgRole,
  onAssignRole,
  onBlock,
  onDeleteUser,
}: AdminRolePendingBlockProps) {
  if (approvedRolePending.length === 0) {
    return <div className="text-sm text-gray-500 py-1">No approved users missing a role.</div>;
  }

  return (
    <div className="space-y-3">
      {approvedRolePending.map((p) => (
        <div key={p.id} className="p-3 rounded-lg border border-gray-200">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="font-medium">{formatAdminProfileName(p)}</div>
              <div className="text-sm text-gray-600">{p.email}</div>
              <div className="text-xs text-gray-500 mt-1">
                domain: {p.email_domain ?? "?"} • last sign-in: {p.last_sign_in_at ?? "?"}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                current role:{" "}
                <span className="font-semibold text-gray-700">
                  {(rolesByUserId.get(p.id) ?? []).join(", ") || "none"}
                </span>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <select
                className="text-sm border border-gray-200 rounded-lg px-2 py-2 bg-white"
                value={getRoleFor(p.id)}
                onChange={(e) => setRoleByUser((prev) => ({ ...prev, [p.id]: e.target.value as OrgRole }))}
              >
                {orgRolesForAddDropdown(canAssignManufacturingOrgRole).map((r) => (
                  <option key={r} value={r}>
                    {ORG_ROLE_LABELS[r]}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => onAssignRole(p.id)}
                className="px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:opacity-60"
                disabled={loading}
              >
                Assign role
              </button>
              <button
                type="button"
                onClick={() => onBlock(p.id, formatAdminProfileName(p))}
                className="px-3 py-2 rounded-lg bg-amber-600 text-white text-sm hover:bg-amber-700 disabled:opacity-60"
                disabled={loading}
              >
                Block
              </button>
              {canDeleteUser && (
                <button
                  type="button"
                  onClick={() => onDeleteUser(p.id, formatAdminProfileName(p))}
                  className="px-3 py-2 rounded-lg border border-red-300 text-red-600 text-sm hover:bg-red-50 disabled:opacity-60"
                  disabled={loading}
                >
                  Delete User
                </button>
              )}
            </div>
          </div>
          <div className="mt-2">
            <input
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2"
              placeholder="Block reason (optional)"
              value={blockReasonByUser[p.id] ?? ""}
              onChange={(e) => setBlockReasonByUser((prev) => ({ ...prev, [p.id]: e.target.value }))}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
